import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Loader2, FileText, Receipt, RotateCcw, ExternalLink, Wallet, ShieldCheck, XCircle } from "lucide-react";

/**
 * CARTAL — user's payment history (`/payments/history`).
 * Lists all payments with quick access to receipt / invoice / refund PDF.
 */
export default function PaymentHistoryPage() {
  const { lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [actingId, setActingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      // iter105 — best-effort reconciliation. If the buyer just paid but
      // /payment/result never got to fire /completed (tab closed early,
      // mobile background kill), Moyasar has the payment as `paid` but
      // our DB still has `initiated`. This call walks recent initiated
      // rows and promotes any that Moyasar confirms as paid.
      try {
        await apiClient.post("/payments/reconcile-mine");
      } catch { /* non-fatal — history load must never fail because reconcile did */ }
      const { data } = await apiClient.get("/payments/mine");
      setRows(data.items || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const captureHold = async (paymentId) => {
    const ok = window.confirm(
      lang === "ar"
        ? "تأكيد الاستلام؟\n\nسيتم تحويل المبلغ المحجوز إلى كارتال. هذا الإجراء نهائي ولا يمكن التراجع عنه."
        : "Confirm receipt?\n\nThe held funds will be transferred to CARTAL. This action is final."
    );
    if (!ok) return;
    setActingId(paymentId);
    try {
      await apiClient.post("/payments/capture", { payment_id: paymentId });
      await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setActingId(null); }
  };

  const voidHold = async (paymentId) => {
    const ok = window.confirm(
      lang === "ar"
        ? "إلغاء الحجز؟\n\nسيُرفع الحجز عن بطاقتك ولن يُخصم أي مبلغ. هذا الإجراء نهائي."
        : "Cancel the hold?\n\nThe reservation on your card will be released; no funds will be captured."
    );
    if (!ok) return;
    setActingId(paymentId);
    try {
      await apiClient.post("/payments/void", { payment_id: paymentId });
      await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setActingId(null); }
  };

  const openDoc = (path) => window.open(path, "_blank", "noopener,noreferrer");

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-10" data-testid="payment-history-page">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline overline-gold">{lang === "ar" ? "سجل الدفعات" : "PAYMENT HISTORY"}</div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-[#0a0a0a]">
            {lang === "ar" ? "دفعاتي وفواتيري" : "My payments & invoices"}
          </h1>
        </div>
        <Link to="/payouts" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border-2 border-[#c9a55c] text-[#8a6f2e] hover:bg-[#f5f0e6]">
          <Wallet className="w-4 h-4" />
          {lang === "ar" ? "حسابات السحب" : "Payout accounts"}
        </Link>
      </div>

      {err && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 mb-4">{err}</div>}

      {loading ? (
        <div className="py-16 text-center text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline me-2" />{lang === "ar" ? "تحميل…" : "Loading…"}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#c9a55c]/40 py-16 text-center">
          <p className="text-[#6b6b6b] text-sm">{lang === "ar" ? "لا توجد دفعات بعد." : "No payments yet."}</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#0a0a0a]/10 bg-white overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#fafaf7] text-[10px] uppercase tracking-[0.2em] text-[#6b6b6b]">
              <tr>
                <th className="text-start p-3">{lang === "ar" ? "التاريخ" : "Date"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الوصف" : "Description"}</th>
                <th className="text-start p-3">{lang === "ar" ? "المبلغ" : "Amount"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الحالة" : "Status"}</th>
                <th className="text-start p-3">{lang === "ar" ? "المستندات" : "Documents"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#0a0a0a]/5 hover:bg-[#fafaf7]" data-testid={`payment-history-row-${r.id}`}>
                  <td className="p-3 text-xs" dir="ltr">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="p-3">
                    <div className="font-semibold text-[#0a0a0a] truncate max-w-[240px]">{r.description}</div>
                    <div className="text-[10px] text-[#8a8a8a]">{r.purpose}{r.sku ? ` · ${r.sku}` : ""}</div>
                  </td>
                  <td className="p-3 font-mono font-bold" dir="ltr">
                    {Number(r.amount_sar).toLocaleString()} SAR
                    {r.refunded_halalas > 0 && (
                      <div className="text-[10px] text-purple-700">-{(r.refunded_halalas/100).toFixed(2)} refunded</div>
                    )}
                  </td>
                  <td className="p-3"><StatusPill status={r.status} lang={lang} /></td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => openDoc(`/api/payments/${r.id}/receipt.html`)}
                              className="inline-flex items-center gap-1 text-xs font-bold text-[#8a6f2e] hover:text-[#c9a55c]"
                              data-testid={`view-receipt-${r.id}`}>
                        <Receipt className="w-3.5 h-3.5" />{lang === "ar" ? "إيصال" : "Receipt"}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                      {r.status === "paid" && (
                        <button onClick={() => openDoc(`/api/payments/${r.id}/invoice.html`)}
                                className="inline-flex items-center gap-1 text-xs font-bold text-[#0a0a0a] hover:text-[#8a6f2e]"
                                data-testid={`view-invoice-${r.id}`}>
                          <FileText className="w-3.5 h-3.5" />{lang === "ar" ? "فاتورة" : "Invoice"}
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                      {r.status === "held" && (
                        <>
                          <button
                            onClick={() => captureHold(r.id)}
                            disabled={actingId === r.id}
                            className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                            data-testid={`capture-hold-${r.id}`}
                          >
                            {actingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                            {lang === "ar" ? "تأكيد الاستلام" : "Confirm receipt"}
                          </button>
                          <button
                            onClick={() => voidHold(r.id)}
                            disabled={actingId === r.id}
                            className="inline-flex items-center gap-1 text-xs font-bold text-red-700 hover:text-red-900 disabled:opacity-50"
                            data-testid={`void-hold-${r.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            {lang === "ar" ? "إلغاء الحجز" : "Cancel hold"}
                          </button>
                        </>
                      )}
                      {(r.refunded_halalas > 0 || r.status === "refunded") && (
                        <button onClick={() => openDoc(`/api/payments/${r.id}/refund-receipt.html`)}
                                className="inline-flex items-center gap-1 text-xs font-bold text-purple-700 hover:text-purple-900"
                                data-testid={`view-refund-${r.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" />{lang === "ar" ? "استرداد" : "Refund"}
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-10 text-center">
        <Link to="/dashboard" className="text-sm text-[#8a6f2e] hover:text-[#c9a55c]">
          ← {lang === "ar" ? "العودة إلى لوحة التحكم" : "Back to dashboard"}
        </Link>
      </div>
    </div>
  );
}

function StatusPill({ status, lang }) {
  const m = {
    paid:      { c: "#22c55e", ar: "مدفوعة", en: "Paid" },
    held:      { c: "#c9a55c", ar: "محجوزة (ضمان)", en: "Held (Escrow)" },
    pending:   { c: "#c9a55c", ar: "قيد المعالجة", en: "Pending" },
    initiated: { c: "#8a8a8a", ar: "بدأت", en: "Initiated" },
    failed:    { c: "#ef4444", ar: "فشلت", en: "Failed" },
    canceled:  { c: "#8a8a8a", ar: "أُلغيت", en: "Canceled" },
    refunded:  { c: "#8b5cf6", ar: "مستردة", en: "Refunded" },
  };
  const s = m[status] || { c: "#8a8a8a", ar: status, en: status };
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
          style={{ backgroundColor: s.c + "22", color: s.c, border: `1px solid ${s.c}55` }}>
      {lang === "ar" ? s.ar : s.en}
    </span>
  );
}
